import { DOCUMENT, Injectable, inject } from '@angular/core';

/** What the font dialog remembers per browser: favourite fonts, the last picks and the language filter. */
export interface FontPickerPreferences {
  favourites: string[];
  recent: string[];
  language: string;
}

/** How many picks the dialog lists under Favorite fonts, as the old plugin's `nrRecents`. */
export const RECENT_PICKS = 3;

const STORAGE_KEY = 'fontPicker';
/** The jQuery plugin kept the same three values in this cookie, sent with every request. */
const LEGACY_COOKIE = 'jqfs';

@Injectable({ providedIn: 'root' })
export class FontPickerPreferencesStore {
  private readonly document = inject(DOCUMENT);
  private cache: FontPickerPreferences | null = null;

  read(): FontPickerPreferences {
    this.cache ??= this.load();
    return this.cache;
  }

  update(change: Partial<FontPickerPreferences>): FontPickerPreferences {
    this.cache = {...this.read(), ...change};
    this.save(this.cache);
    return this.cache;
  }

  /**
   * Puts a pick first among the last picks. The plugin left a re-picked font where it was, so its list could drop a
   * newer pick for an older one; this one is the last 3 picks, newest first.
   */
  addRecent(family: string): void {
    this.update({recent: [family, ...this.read().recent.filter((recent) => recent !== family)].slice(0, RECENT_PICKS)});
  }

  private load(): FontPickerPreferences {
    try {
      const stored = this.document.defaultView?.localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const {favourites, recent, language} = JSON.parse(stored);
        return {
          favourites: strings(favourites),
          recent: strings(recent).slice(0, RECENT_PICKS),
          language: typeof language === 'string' ? language : '',
        };
      }
    } catch {
      // Storage blocked or the entry unreadable: start from the cookie, or empty.
    }
    const legacy = this.readLegacyCookie();
    if (legacy && this.save(legacy)) {
      this.document.cookie = `${LEGACY_COOKIE}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
    }
    return legacy ?? {favourites: [], recent: [], language: ''};
  }

  private save(preferences: FontPickerPreferences): boolean {
    try {
      this.document.defaultView?.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
      return true;
    } catch {
      return false;
    }
  }

  /**
   * The plugin's cookie: `key|value` pairs joined by `||`, URI-encoded, where favourites and recent picks are
   * comma-separated `google:Family` or `local:Family` entries.
   */
  private readLegacyCookie(): FontPickerPreferences | null {
    const match = new RegExp(`(?:^|; )${LEGACY_COOKIE}=([^;]*)`).exec(this.document.cookie);
    if (!match) {
      return null;
    }
    const jar: Record<string, string> = {};
    let raw = '';
    try {
      raw = decodeURIComponent(match[1]);
    } catch {
      return null;
    }
    for (const pair of raw.split('||')) {
      const at = pair.indexOf('|');
      if (at > 0) {
        jar[pair.slice(0, at)] = pair.slice(at + 1);
      }
    }
    const families = (list: string | undefined) => (list ? list.split(',') : [])
      .map((entry) => entry.slice(entry.indexOf(':') + 1))
      .filter(Boolean);
    return {favourites: families(jar['favs']), recent: families(jar['recents']).slice(0, RECENT_PICKS), language: jar['lang'] ?? ''};
  }
}

const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
